/**
 * Deep-linking into the rates list. Contract links and Kai "Open in Rates"
 * write `/rates?contract_ref=…` (and optional carrier / include_inactive);
 * the rates page hydrates its property-filter bar from those params.
 */
import {
  EMPTY_PROPERTY_FILTER_QUERY,
  KAI_TABLE_FILTERS_EVENT,
  newTokenId,
  serializePropertyFilterQuery,
  type FilterOperator,
  type PropertyFilterQuery,
  type PropertyFilterToken,
} from "@/components/table";

export const RATES_FILTER_STORAGE_KEY = "twido-v2-rate-cards-filters";

const TEXT_KEYS = ["carrier", "contract_ref", "named_account", "status"] as const;

const NON_TOKEN_KEYS = new Set([
  "page",
  "per_page",
  "sort_by",
  "sort_dir",
  "source_type",
  "include_inactive",
]);

export interface RatesFilterLinkInput {
  carrier?: string | null;
  contract_ref?: string | null;
  named_account?: string | null;
  /** When true, the rates page includes superseded / expired cards. */
  include_inactive?: boolean;
}

function token(
  propertyKey: string,
  operator: FilterOperator,
  value: string,
): PropertyFilterToken {
  return {
    id: newTokenId(),
    propertyKey,
    operator,
    value,
    valueLabel: value,
  };
}

function persistRatesQuery(query: PropertyFilterQuery) {
  try {
    if (query.tokens.length === 0) {
      sessionStorage.removeItem(RATES_FILTER_STORAGE_KEY);
    } else {
      sessionStorage.setItem(
        RATES_FILTER_STORAGE_KEY,
        serializePropertyFilterQuery(query),
      );
    }
  } catch {
    // Non-fatal: URL hydration still applies the filters.
  }

  try {
    window.dispatchEvent(
      new CustomEvent(KAI_TABLE_FILTERS_EVENT, {
        detail: { storageKey: RATES_FILTER_STORAGE_KEY, query },
      }),
    );
  } catch {
    // Non-fatal.
  }
}

/** Pure `/rates?…` href. Does not touch sessionStorage. */
export function ratesFilterHref(filters: RatesFilterLinkInput): string {
  const params = new URLSearchParams();
  const contractRef = filters.contract_ref?.trim();
  const carrier = filters.carrier?.trim();
  const namedAccount = filters.named_account?.trim();

  if (carrier) params.set("carrier", carrier);
  if (contractRef) params.set("contract_ref", contractRef);
  if (namedAccount) params.set("named_account", namedAccount);
  if (filters.include_inactive) params.set("include_inactive", "1");

  const query = params.toString();
  return query ? `/rates?${query}` : "/rates";
}

/** Build a `/rates?…` URL and persist the same tokens for the filter bar. */
export function buildRatesFilterUrl(filters: RatesFilterLinkInput): string {
  const href = ratesFilterHref(filters);
  const params = new URLSearchParams(href.split("?")[1] ?? "");
  persistRatesQuery(searchParamsToRatesQuery(params));
  return href;
}

export function searchParamsToRatesQuery(
  params: URLSearchParams,
): PropertyFilterQuery {
  const tokens: PropertyFilterToken[] = [];

  for (const key of TEXT_KEYS) {
    const value = params.get(key)?.trim();
    if (!value) continue;
    tokens.push(token(key, "=", value));
  }

  if (tokens.length === 0) return EMPTY_PROPERTY_FILTER_QUERY;

  return { operation: "and", tokens };
}

export function includeInactiveFromParams(params: URLSearchParams): boolean {
  const value = params.get("include_inactive")?.trim().toLowerCase();
  return value === "1" || value === "true" || value === "yes";
}

export function hasRatesFilterParams(params: URLSearchParams): boolean {
  for (const key of params.keys()) {
    if (NON_TOKEN_KEYS.has(key)) continue;
    return true;
  }
  return includeInactiveFromParams(params);
}

export interface RatesUrlHydration {
  query: PropertyFilterQuery;
  includeInactive: boolean;
}

export function parseRatesUrlParams(params: URLSearchParams): RatesUrlHydration {
  return {
    query: searchParamsToRatesQuery(params),
    includeInactive: includeInactiveFromParams(params),
  };
}
